import { useMemo } from 'react';

import { ProductSlider } from '../ProductSlider/ProductSlider';
import { localizeCategoryLabel } from '../../../utils/category-labels';
import { t as uiT } from '../../../utils/ui-strings';
import { OrderCardList, type OrderCardAction } from '../OrderCardList/OrderCardList';
import { OrderInfoCard } from '../OrderInfoCard/OrderInfoCard';
import { OrderTrackingCard } from '../OrderTrackingCard/OrderTrackingCard';
import { OrderCard } from '../order-card/OrderCard';
import { type SuggestedAction } from '../../../types/ai';
import { EmailDraftCard } from '../EmailDraftCard';
import { cn } from '../../../utils/cn';
import { FeedbackButtons as AdminFeedbackButtons } from '../../feedback/FeedbackButtons';

import { isWelcomeMessage, type ChatMessageProps, type ProductSliderProductView } from './types';
import { CART_ADD_ACTION_PREFIX } from './constants';
import { shouldShowOutfitSlider } from './outfit-gate';
import { formatTimestamp } from './utils/format-timestamp';
import { cleanSuggestedActionsFromContent } from './parsers/suggested-actions';
import { parseProductCards, mapStructuredProductCards } from './parsers/product-cards';
import { parseDetailsBlock } from './parsers/details-block';
import { textBeforeEmailDraft } from './parsers/email-draft-text';
import {
  parseOrderCard,
  parseOrderList,
  parseOrderInfo,
  parseOrderTracking,
  stripAsciiSeparators,
  stripDuplicateOrderSections,
} from './parsers/order-blocks';
import { extractOrderRowsForTable } from './parsers/order-context';
import {
  buildFallbackProductActionButton,
  buildProductActionButtons,
  hasProductSizes,
  hydrateMissingProductIdsFromActions,
} from './actions/product-actions';
import {
  matchSuggestedActionsToCards,
  matchSuggestedActionsToStructuredOutfit,
} from './actions/action-matching';
import { FeedbackButtons } from './parts/FeedbackButtons';
import { OrderContextViewer } from './parts/OrderContextViewer';
import { SourcesList } from './parts/SourcesList';
import { SuggestedActions } from './parts/SuggestedActions';
import { WelcomeEditorialLinks } from './parts/WelcomeEditorialLinks';
import { ProductCardsGrid } from './parts/ProductCardsGrid';
import { MessageMarkdown } from './parts/MessageMarkdown';
import { MessageDetailsPanel } from './parts/MessageDetailsPanel';
import { AnnouncementBanner } from './parts/AnnouncementBanner';

export type { ChatMessageItem, ChatRole } from './types';

export const ChatMessage = ({
  message,
  onSuggestedActionClick,
  isLatestAssistant,
  sessionId,
  channel,
  showAdminFeedback,
  onAdminFeedback,
  onEmailDraftResolved,
}: ChatMessageProps) => {
  const isUser = message.role === 'user';
  const timestamp = formatTimestamp(message.createdAt);
  const isError = message.status === 'error';
  const isWelcomePlaceholder = isWelcomeMessage(message);

  const {
    productCards,
    hasStructuredProductCards,
    orderCardList,
    orderInfoCard,
    orderTrackingCard,
    orderCardBlock,
    displayContent,
    detailsContent,
    detailsStreamingPartial,
  } = useMemo(() => {
    const withoutSuggestedActions = cleanSuggestedActionsFromContent(message.content);
    const structuredCards = mapStructuredProductCards(message.productCards);
    const { cards: parsedCards, cleanedText: cleanedAfterCards } =
      parseProductCards(withoutSuggestedActions);
    const cards = hydrateMissingProductIdsFromActions(
      structuredCards.length > 0 ? structuredCards : parsedCards,
      message.suggestedActions,
    );
    const { orderCard, cleanedText: cleanedAfterOrderCard } = parseOrderCard(cleanedAfterCards);
    const { orderList, cleanedText: cleanedAfterOrderList } = parseOrderList(cleanedAfterOrderCard);
    const { orderInfo, cleanedText: cleanedAfterOrderInfo } = parseOrderInfo(cleanedAfterOrderList);
    const { orderTracking, cleanedText: cleanedAfterTracking } =
      parseOrderTracking(cleanedAfterOrderInfo);
    const {
      details,
      cleanedText: cleanedAfterDetails,
      isStreamingPartial,
    } = parseDetailsBlock(cleanedAfterTracking);
    return {
      productCards: cards,
      hasStructuredProductCards: structuredCards.length > 0,
      orderCardBlock: orderCard,
      orderCardList: orderList,
      orderInfoCard: orderInfo,
      orderTrackingCard: orderTracking,
      displayContent: cleanedAfterDetails.trim(),
      detailsContent: details,
      detailsStreamingPartial: isStreamingPartial,
    };
  }, [message.content, message.productCards, message.suggestedActions]);

  const hasStructuredOutfitMetadata =
    !isUser && Array.isArray(message.outfitProducts) && message.outfitProducts.length > 0;

  const { cardActions, remainingSuggestedActions } = useMemo(() => {
    const suggestedActions = Array.isArray(message.suggestedActions)
      ? message.suggestedActions
      : [];
    if (hasStructuredOutfitMetadata || productCards.length === 0 || suggestedActions.length === 0) {
      return {
        cardActions: productCards.map(() => [] as SuggestedAction[]),
        remainingSuggestedActions: suggestedActions,
      };
    }

    const { cardActions: matchedActions, consumedActionIndexes } = matchSuggestedActionsToCards(
      productCards,
      suggestedActions,
    );

    return {
      cardActions: matchedActions,
      remainingSuggestedActions: suggestedActions.filter(
        (_, index) => !consumedActionIndexes.has(index),
      ),
    };
  }, [hasStructuredOutfitMetadata, message.suggestedActions, productCards]);

  const hasProducts = !isUser && productCards.length > 0;
  const { structuredOutfitProducts, structuredOutfitActions, displaySuggestedActions } =
    useMemo(() => {
      const sourceProducts =
        !isUser && Array.isArray(message.outfitProducts)
          ? message.outfitProducts
              .map((product) => {
                const productId = String(product.id ?? '').trim();
                const slot = String(product.categoryKey ?? product.role ?? '').trim();
                const view: ProductSliderProductView = {
                  id: productId || undefined,
                  selectionKey: `${message.id}:${slot || 'outfit'}:${productId || product.name}`,
                  name: product.name,
                  brand: product.brand,
                  price: product.price,
                  currency: product.currency,
                  image: product.imageUrl,
                  url: product.url,
                  sizes: product.sizes,
                  slot: slot || undefined,
                  tryOn: product.tryOn,
                  tryOnFraming: product.tryOnFraming,
                  tagLabel: localizeCategoryLabel(
                    product.categoryKey ?? product.role,
                    product.categoryLabel,
                    message.detectedLanguage,
                  ),
                };
                return { product, view };
              })
              .filter((item) => Boolean(item.view.name && item.view.image))
          : [];

      if (sourceProducts.length === 0 || remainingSuggestedActions.length === 0) {
        return {
          structuredOutfitProducts: sourceProducts.map((item) => {
            const slot = String(item.product.categoryKey ?? item.product.role ?? '').trim();
            const productId = String(item.product.id ?? '').trim();
            const hasSizes = hasProductSizes(item.view);
            const fallbackActionButton =
              hasSizes && productId
                ? buildFallbackProductActionButton(
                    productId,
                    slot || undefined,
                    true,
                    message.detectedLanguage,
                  )
                : undefined;

            return {
              ...item.view,
              actionButtons: fallbackActionButton ? [fallbackActionButton] : undefined,
            };
          }),
          structuredOutfitActions: sourceProducts.map(() => [] as SuggestedAction[]),
          displaySuggestedActions: remainingSuggestedActions,
        };
      }

      const { productActions, consumedActionIndexes } = matchSuggestedActionsToStructuredOutfit(
        sourceProducts.map((item) => item.product),
        remainingSuggestedActions,
      );

      return {
        structuredOutfitProducts: sourceProducts.map((item, index) => {
          const slot = String(item.product.categoryKey ?? item.product.role ?? '').trim();
          const productId = String(item.product.id ?? '').trim();
          const actionButtons = buildProductActionButtons(
            productActions[index] ?? [],
            slot || undefined,
            productId || undefined,
            message.detectedLanguage,
          );
          const hasSizes = hasProductSizes(item.view);
          const hasCartButton = actionButtons.some((button) =>
            String(button.value).startsWith(CART_ADD_ACTION_PREFIX),
          );
          const fallbackActionButton =
            !hasCartButton && hasSizes && productId
              ? buildFallbackProductActionButton(
                  productId,
                  slot || undefined,
                  true,
                  message.detectedLanguage,
                )
              : undefined;

          return {
            ...item.view,
            actionButtons:
              fallbackActionButton && actionButtons.length > 0
                ? [...actionButtons, fallbackActionButton]
                : fallbackActionButton
                  ? [fallbackActionButton]
                  : actionButtons.length > 0
                    ? actionButtons
                    : undefined,
          };
        }),
        structuredOutfitActions: productActions,
        displaySuggestedActions: remainingSuggestedActions.filter(
          (_, index) => !consumedActionIndexes.has(index),
        ),
      };
    }, [
      isUser,
      message.id,
      message.detectedLanguage,
      message.outfitProducts,
      remainingSuggestedActions,
    ]);
  const hasStructuredOutfitProducts =
    hasStructuredOutfitMetadata && structuredOutfitProducts.length > 0;
  const showOutfitSlider = shouldShowOutfitSlider({
    hasOutfitProducts: hasStructuredOutfitProducts,
    hasStructuredProductCards,
    isOutfitBuilding: message.isOutfitBuilding,
    isProductSearching: message.isProductSearching,
    searchedProducts: message.searchedProducts,
  });

  const handleStructuredOutfitAction = (value: string) => {
    const flattenedActions = structuredOutfitActions.flat();
    const fromSuggested = flattenedActions.find((action) => action.value === value);
    if (fromSuggested) {
      onSuggestedActionClick?.(fromSuggested);
      return;
    }

    const renderedLabel = structuredOutfitProducts
      .flatMap((product) => product.actionButtons ?? [])
      .find((button) => button.value === value)?.label;
    onSuggestedActionClick?.({ label: renderedLabel ?? value, value });
  };

  const handleOrderCardAction = (action: OrderCardAction) =>
    onSuggestedActionClick?.({
      label: action.label,
      value: action.value,
      actionId: action.actionId,
    });

  const orderTable = useMemo(
    () => extractOrderRowsForTable(message.orderContext),
    [message.orderContext],
  );
  const hasAnySlider = showOutfitSlider || hasProducts;
  const finalDisplayContent = useMemo(() => {
    let cleaned = stripAsciiSeparators(displayContent);
    if (orderTable.rows.length > 0) {
      cleaned = stripDuplicateOrderSections(cleaned);
    }
    return cleaned;
  }, [displayContent, orderTable.rows.length]);
  const emailDraftCard = !isUser ? message.emailDraft : undefined;
  const bubbleContent = emailDraftCard
    ? textBeforeEmailDraft(finalDisplayContent, emailDraftCard.labels)
    : finalDisplayContent;
  const shouldRenderTextBubble = isUser || bubbleContent.trim().length > 0 || isError;

  return (
    <div className={cn('flex w-full gap-3', isUser ? 'flex-row-reverse' : 'flex-row')}>
      <div
        className={cn(
          'flex max-w-[88%] flex-col gap-2.5',
          isUser ? 'items-end' : 'items-start',
          hasAnySlider && 'w-full',
        )}
      >
        {shouldRenderTextBubble ? (
          <div
            className={cn(
              'overflow-hidden rounded-sm border px-4 py-3 text-[15px] leading-7 whitespace-pre-wrap break-words',
              isUser
                ? 'border-brand-blue bg-brand-blue text-white'
                : isError
                  ? 'border-red-300 bg-red-50 text-red-800'
                  : 'border-black/10 bg-basic-white text-brand-blue',
            )}
          >
            {isUser && message.imageUrl ? (
              <img
                src={message.imageUrl}
                alt="Uppladdad bild"
                className="mb-2 max-h-56 w-auto max-w-full rounded-sm object-contain"
              />
            ) : null}
            <MessageMarkdown isUser={isUser}>{bubbleContent}</MessageMarkdown>
            {!isUser && isWelcomePlaceholder ? (
              <WelcomeEditorialLinks actions={message.suggestedActions ?? []} />
            ) : null}
          </div>
        ) : null}

        {!isUser && isWelcomePlaceholder && message.announcement ? (
          <AnnouncementBanner banner={message.announcement} />
        ): null}

        {emailDraftCard && sessionId ? (
          <EmailDraftCard
            card={emailDraftCard}
            sessionId={sessionId}
            interactive={Boolean(isLatestAssistant)}
            onResolved={(resolvedMessage, status) =>
              onEmailDraftResolved?.(resolvedMessage, status)
            }
          />
        ) : null}

        {/* .ai-chat är widgetens rot — kortens kopierade CSS är skalad under den. */}
        {!isUser && (orderCardBlock || orderCardList?.length || orderInfoCard || orderTrackingCard) ? (
          <div className="ai-chat w-full">
            {orderCardBlock ? <OrderCard block={orderCardBlock} onAction={handleOrderCardAction} /> : null}
            {orderCardList && orderCardList.length > 0 ? (
              <OrderCardList orders={orderCardList} onActionClick={handleOrderCardAction} />
            ) : null}
            {orderInfoCard ? (
              <OrderInfoCard info={orderInfoCard} onAction={handleOrderCardAction} />
            ) : null}
            {orderTrackingCard ? (
              <OrderTrackingCard data={orderTrackingCard} onAction={handleOrderCardAction} />
            ) : null}
          </div>
        ) : null}

        {showOutfitSlider ? (
          <div className="w-full">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-grey">
              {uiT('outfit_suggestion', message.detectedLanguage)}
            </p>
            <ProductSlider
              products={structuredOutfitProducts}
              onActionClick={handleStructuredOutfitAction}
              lang={message.detectedLanguage}
              expectedCount={
                message.isOutfitBuilding
                  ? message.outfitSlotsExpected && message.outfitSlotsExpected > 0
                    ? message.outfitSlotsExpected
                    : 5
                  : undefined
              }
            />
          </div>
        ) : null}

        {!showOutfitSlider && (hasProducts || message.isProductSearching) && (
          <ProductCardsGrid
            cards={productCards}
            cardActions={cardActions}
            onActionClick={onSuggestedActionClick}
            lang={message.detectedLanguage}
            expectedCount={
              message.isProductSearching
                ? message.productCardsExpected && message.productCardsExpected > 0
                  ? message.productCardsExpected
                  : 3
                : undefined
            }
          />
        )}

        {!isUser ? (
          <div className="flex w-full flex-col gap-2">
            <MessageDetailsPanel
              messageId={message.id}
              detailsContent={detailsContent}
              detailsStreamingPartial={detailsStreamingPartial}
            />
            {isLatestAssistant &&
            !emailDraftCard &&
            displaySuggestedActions.length > 0 &&
            onSuggestedActionClick ? (
              <SuggestedActions
                actions={displaySuggestedActions}
                onActionClick={onSuggestedActionClick}
                hideExternalLinks={isWelcomePlaceholder}
              />
            ) : null}
            <SourcesList sources={message.sources} />
            <OrderContextViewer orderContext={message.orderContext} />
            {sessionId ? (
              <FeedbackButtons sessionId={sessionId} message={message} channel={channel} />
            ) : null}
            {showAdminFeedback && onAdminFeedback ? (
              <AdminFeedbackButtons onFeedback={onAdminFeedback} />
            ) : null}
          </div>
        ) : null}

        {timestamp ? <span className="text-[11px] text-text-grey/80">{timestamp}</span> : null}
      </div>
    </div>
  );
};
